
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

export function useSharedPersistentState<T>(
    key: string,
    defaultValue: T
): [T, React.Dispatch<React.SetStateAction<T>>] {
    const queryClient = useQueryClient();


    const { data: state } = useQuery({
        queryKey: ['persistent-state', key],
        queryFn: () => {
            try {
                const storedValue = localStorage.getItem(key);
                return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
            } catch (error) {
                console.warn(`Failed to parse localStorage value for key "${key}":`, error);
                return defaultValue;
            }
        },
        staleTime: Infinity, // Data never goes stale
        gcTime: Infinity,    // Keep in cache forever
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    // Enhanced setState that updates both query cache and localStorage
    const setState = useCallback<React.Dispatch<React.SetStateAction<T>>>(
        (value) => {

            const currentState = queryClient.getQueryData<T>(['persistent-state', key]) ?? defaultValue;

            const newValue = typeof value === 'function'
                ? (value as (prevState: T) => T)(currentState)
                : value;

            try {

                localStorage.setItem(key, JSON.stringify(newValue));

                queryClient.setQueryData(['persistent-state', key], newValue);
            } catch (error) {
                console.error(`Failed to update localStorage for key "${key}":`, error);
            }
        },
        [key, defaultValue, queryClient]
    );

    return [state ?? defaultValue, setState];
}

